import React from 'react';
import useListEnvironments from '../../hooks/useListEnvironments';
import SimpleLoader from 'components/loader/SimpleLoader';
import { DatasetCreateFormData } from 'types/Forms';
import useNewDataset from 'views/Datasets/hooks/createDataset';
import NInputWithChips from 'components/Inputs/Labels Input/NInputWithChips';
import NSelectTooltip from 'components/Inputs/InputsWithTooltip/NSelectTooltip';
import NInputTooltip from 'components/Inputs/InputsWithTooltip/NInputTooltip';
import NCheckboxTooltip from 'components/Inputs/InputsWithTooltip/NCheckboxTooltip';
import AutocompleteTooltip, {
    Option,
} from 'components/Inputs/InputsWithTooltip/AutocompleteTooltip';
import { EnvironmentSelectItem } from 'types/Environment';
import { NButton } from '@innersource-nn/novoui-react';
import { SearchFilter } from 'types/Filters';

const filter: SearchFilter = {
    roles: ['Admin', 'Owner', 'Invited', 'DatasetCreator'],
    page: 1,
    pageSize: 20,
    onlyProduction: true,
    samlgroup: ['admin'],
};
interface ThirdStepProps {
    importing: boolean;
    setActiveStep: React.Dispatch<React.SetStateAction<number>>;
    formData: DatasetCreateFormData;
    setFormData: React.Dispatch<React.SetStateAction<DatasetCreateFormData>>;
    setNewDataset: React.Dispatch<
        React.SetStateAction<{
            datasetUri: string;
            label: string;
        }>
    >;
    setCreationError: React.Dispatch<React.SetStateAction<string>>;
}

export default function ThirdStep({
    importing,
    formData,
    setFormData,
    setActiveStep,
    setNewDataset,
    setCreationError,
}: ThirdStepProps) {
    const { environments, environmentError, rolesError, loading, roles } =
        useListEnvironments(formData.environmentUri, filter);
    const { creationLoading, createNewDataset, importNewDataset } =
        useNewDataset(setCreationError);

    const isButtonDisabled = () => {
        const {
            SamlAdminGroupName,
            environmentUri,
            businessOwnerEmail,
            bucketName,
        } = formData;
        if (
            SamlAdminGroupName !== '' &&
            environmentUri !== '' &&
            businessOwnerEmail !== ''
        ) {
            if (importing) return bucketName === '' ?? false;
            else return false;
        }
        return true;
    };

    const updateLabels = (inputName: string, newValue: string[]) => {
        setFormData((prevData: any) => {
            return {
                ...prevData,
                [inputName]: newValue,
            };
        });
    };

    const handleOwnerInitials = () => {
        let newValue = formData.businessOwnerEmail;
        if (!newValue.includes('@') && newValue !== '') {
            newValue += '@novonordisk.com';
        }
        setFormData({
            ...formData,
            businessOwnerEmail: newValue,
            owner: newValue,
        });
    };
    const handleChange = async (
        event: React.ChangeEvent<HTMLSelectElement>,
    ) => {
        const { name, value } = event.target;
        if (name === 'environment') {
            const selectedEnvironment = environments.find(
                (option) => option.key === value,
            );

            if (selectedEnvironment) {
                setFormData({
                    ...formData,
                    organizationUri: selectedEnvironment.organizationUri,
                    environmentUri: selectedEnvironment.key,
                    SamlAdminGroupName: '',
                });
            }
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
    };

    async function handleClick() {
        let newDatasetInfo = { datasetUri: '', label: '' };
        if (importing) {
            newDatasetInfo = await importNewDataset(formData);
        } else {
            newDatasetInfo = await createNewDataset(formData);
        }
        setNewDataset(newDatasetInfo);
        setActiveStep(3);
    }

    function handleSelectEnvironment(selectedEnvironment: Option) {
        //implement the logic to handle the selected environment as in handlechange
        const { key, organizationUri } =
            selectedEnvironment as EnvironmentSelectItem;
        setFormData({
            ...formData,
            organizationUri,
            environmentUri: key,
            SamlAdminGroupName: '',
        });
    }

    return (
        <div>
            {loading ? (
                <SimpleLoader />
            ) : (
                <div className="flex flex-col gap-8 mr-24">
                    <AutocompleteTooltip
                        label="Environment*"
                        placeholder="Search for environment...."
                        tooltip="Select the environment where the dataset will be deployed in."
                        items={environments}
                        handleSelected={handleSelectEnvironment}
                        error={environmentError}
                    />

                    <div className="grid grid-cols-2 gap-8">
                        <NSelectTooltip
                            content="Select the admin group responsible for managing permissions and access controls."
                            label="Admin group*"
                            size="medium"
                            variant="outline"
                            name="SamlAdminGroupName"
                            options={roles}
                            disabled={
                                (roles.length === 0 &&
                                    formData.SamlAdminGroupName === '') ||
                                environmentError !== ''
                            }
                            modelValue={formData.SamlAdminGroupName}
                            onChange={handleChange}
                            placeholder="Select group"
                            invalid={rolesError !== ''}
                            assistiveText={rolesError}
                        />
                        <NInputTooltip
                            content="Designate the business owner who holds primary responsiblity for the dataset."
                            label="Business Owner*"
                            size="medium"
                            variant="outline"
                            placeholder="Use NN initials"
                            modelValue={formData.businessOwnerEmail}
                            onBlur={handleOwnerInitials}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    businessOwnerEmail: e.target.value,
                                })
                            }
                        />
                        {/* <NSelectTooltip
                            content="Select the domain which is most relevant to the new dataset, if the domain does not exist please contact the datahub support team."
                            label="Data Domain"
                            size="medium"
                            variant="outline"
                            disabled={true}
                            placeholder="Select domain"
                            modelValue={[]}
                        /> */}
                    </div>
                    <NInputWithChips
                        modelValue=""
                        content="Assign data stewards who holds responsibility for the dataset access management"
                        name="businessOwnerDelegationEmails"
                        label="Add Stewards responsible for dataset"
                        placeholder="Type and press enter... (Use NN initials)"
                        updateLabels={updateLabels}
                        labels={formData.businessOwnerDelegationEmails}
                    />
                    <NInputWithChips
                        modelValue=""
                        content="Add descriptive tags or keywords to categorize your datasets using meaningful words."
                        name="tags"
                        label="Tags"
                        placeholder="Type and press enter... "
                        updateLabels={updateLabels}
                        labels={formData.tags}
                    />
                    {importing && (
                        <NInputTooltip
                            content="Insert the name of the Amazon S3 bucket where the data is stored. Bucket name should look like 'dh[dataset name][uri][AWS region]' without spaces and special characters"
                            label="Amazon S3 bucket name*"
                            size="medium"
                            variant="outline"
                            placeholder="Insert link here..."
                            modelValue={formData.bucketName}
                            invalid={formData.bucketName?.includes(':')}
                            assistiveText={
                                formData.bucketName?.includes(':')
                                    ? "Bucket name should look like 'dh[dataset name][uri][AWS region]' without spaces and special characters"
                                    : ''
                            }
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    bucketName: e.target.value,
                                })
                            }
                        />
                    )}
                    <div className="flex gap-16">
                        {!importing && (
                            <div className="flex">
                                <NCheckboxTooltip
                                    label="Enable Crawler"
                                    content="Select to enable automated crawling of data source to populate and update the dataset."
                                    name="enableCrawler"
                                    checkboxSize="medium"
                                    checked={formData.enableCrawler}
                                    onChange={() =>
                                        setFormData({
                                            ...formData,
                                            enableCrawler:
                                                !formData.enableCrawler,
                                        })
                                    }
                                />
                            </div>
                        )}

                        {!importing && (
                            <div className="flex">
                                <NCheckboxTooltip
                                    label="Enable Read Logs Tracking"
                                    content="Enable this setting allows AWS CloudTrail to track and log all read access to files in your S3 bucket. This ensures compliance with GDPR by providing detailed records of who has accessed your data."
                                    name="enableReadTrail"
                                    checkboxSize="medium"
                                    checked={formData.enableReadTrail}
                                    onChange={() =>
                                        setFormData({
                                            ...formData,
                                            enableReadTrail:
                                                !formData.enableReadTrail,
                                        })
                                    }
                                />
                            </div>
                        )}

                        {!importing && (
                            <div className="flex justify-start">
                                <NCheckboxTooltip
                                    content="Select to enable this dataset to be deployed to all the accounts within the environment the dataset belongs to and not only to production"
                                    label="Deploy to all accounts"
                                    name="datasetStageOptions"
                                    checkboxSize="medium"
                                    checked={
                                        formData.datasetStageOptions !== 'PROD'
                                    }
                                    onChange={() =>
                                        setFormData({
                                            ...formData,
                                            datasetStageOptions:
                                                formData.datasetStageOptions ===
                                                'PROD'
                                                    ? 'ALL'
                                                    : 'PROD',
                                        })
                                    }
                                />
                            </div>
                        )}
                        {/* THIS WILL BE DISABLED TILL THE BACKEND FOR IT IS READY
             <div className="flex justify-start">
              <CheckboxInput
                label="Test Dataset"
                disabled={true}
                size="small"
                handleChange={() =>
                  setFormData({
                    ...formData
                  })
                }
              />
              <NTooltip
                placement="up"
                content="Select to indicate that this dataset is made for testing purposes."
              />
            </div>*/}
                    </div>
                    <div>
                        <NButton
                            variant="fill"
                            square
                            className="bg-sea-blue-600 mt-8"
                            onClick={handleClick}
                            disabled={isButtonDisabled()}
                            isLoading={creationLoading}
                        >
                            Create Dataset
                        </NButton>
                    </div>
                </div>
            )}
        </div>
    );
}
